import { Component, Input } from "@angular/core";
import * as moment from "moment";
import { ExcelReportService } from "projects/rms/app/reports/shared/excel-report.service";
import { ReportItemModel } from "projects/rms/app/api/models/report-item-model";
import { UserPreferencesService } from "projects/shared/core/services/user-preferences.service";

@Component({
  selector: "rms-report-data-list",
  templateUrl: "./report-data-list.component.html",
  styleUrls: ["./report-data-list.component.css"],
})
export class ReportDataListComponent {
  @Input() set model(value) {
    this.reportDataList = value;
    this.recordCount = value.length;
    this.first = 0;
  }
  @Input() set report(value) {
    if (value && value.id) {
      this.reportItem = value;
      this.reportColumns = this.reportItem.reportFields
        .filter((column) => column.displayResults)
        .sort((a, b) => {
          if (a.displayOrder > b.displayOrder) return 1;
          if (a.displayOrder < b.displayOrder) return -1;
          return 0;
        });
    }
  }
  @Input() isLoading;

  reportDataList: any[];
  reportItem: ReportItemModel;
  reportColumns: any[];
  recordCount: number;
  first: number = 0;
  pageSizeOptions = [5, 10, 25, 50, { showAll: "All" }];
  pageSize = null;
  tablePrefKey = "reportViewerPageSize";

  financeFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  constructor(
    private _excelReportService: ExcelReportService,
    private _userPreferencesService: UserPreferencesService
  ) {
    this._userPreferencesService.get(this.tablePrefKey).subscribe((data) => {
      this.pageSize = +data?.preferenceValue || 10;
    });
  }

  exportToExcel() {
    let dataForExcel: any[] = [];

    // set row value of report depending on formatType
    this.reportDataList.forEach((row): any => {
      let rowValue = {};
      this.reportColumns.forEach((col) => {
        switch (col.formatType) {
          case "boolean":
            rowValue[col.fieldName] = row[col.fieldName] ? "Yes" : "No";
            break;
          case "date":
          case "timestamp without time zone":
            rowValue[col.fieldName] =
              row[col.fieldName] != null ? new Date(row[col.fieldName]) : null;
            break;
          case "money":
          case "decimal(2)":
            rowValue[col.fieldName] =
              row[col.fieldName] != null
                ? Number(parseFloat(row[col.fieldName]).toFixed(2))
                : null;
            break;
          case "int":
            rowValue[col.fieldName] = Number(parseInt(row[col.fieldName]));
            break;
          default:
            rowValue[col.fieldName] = row[col.fieldName];
            break;
        }
      });
      dataForExcel.push(Object.values(rowValue));
    });

    // set column format depending on formatType
    let columnHeader: any[] = [];
    let columnFormat: string[] = [];
    this.reportColumns.forEach((col): any => {
      let numFmt: string = "";
      columnHeader.push({ name: col.fieldDisplay || col.fieldName });
      switch (col.formatType) {
        case "money":
          numFmt = '"$"#,##0.00';
          break;
        case "date":
          numFmt = "mm/dd/yyyy";
          break;
        case "decimal(2)":
          numFmt = "0.00";
          break;
        case "int":
          numFmt = "0";
          break;
        case "timestamp without time zone":
          numFmt = "mm/dd/yyyy hh:mm:ss";
          break;
        default:
          numFmt = "@";
          break;
      }
      columnFormat.push(numFmt);
    });

    // remove disallowed characters from the title
    let disallowed = /[\\\/\[\]\?\:\*]/g;
    let cleanTitle = this.reportItem.reportTitle.replace(disallowed, "");

    let excelData = {
      title: cleanTitle,
      titleWithDate:
        this.reportItem.reportTitle +
        " (" +
        new Date().toLocaleDateString() +
        ")",
      data: dataForExcel,
      columns: columnHeader,
      formats: columnFormat,
    };
    this._excelReportService.exportExcel(excelData);
  }

  formatColumn(col: any, data: any): string {
    return this.getFormattedText(col.formatType, data);
  }

  private getFormattedText(formatType: string, data: any): string {
    if (formatType && data) {
      switch (formatType) {
        case "date":
          return moment(data).format("MM/DD/YYYY");
        case "timestamp without time zone":
          return moment.utc(data).local().format("MM/DD/YYYY HH:mm:ss");
        case "money":
          return this.financeFormatter.format(data);
        case "decimal(2)":
          return this.numberFormat(data, 2);
        case "int":
          return this.numberFormat(data, 0);
        default:
          return data;
      }
    }
    return data;
  }

  onPage(event) {
    if (event.rows != this.pageSize) {
      if (!this.pageSizeOptions.find((x) => x === event.rows)) event.rows = 50; //if pageSize is not one of the available options, then the user's pick was the "all" option
      this._userPreferencesService.set(this.tablePrefKey, event.rows + "");
      this.pageSize = event.rows;
    }
  }

  linkParameter(col, data): string {
    return data[col.linkParameterField];
  }

  private numberFormat(x, digits): string {
    return Number.parseFloat(x).toFixed(digits);
  }
}
